import { useMutation, useLazyQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { customFieldValidations } from "validations/customFieldValidations";
import {
  CREATE_CUSTOM_FIELD_SETTING,
  GET_CUSTOM_FIELD_SETTING,
  UPDATE_CUSTOM_FIELD_SETTING,
  UPDATE_CUSTOM_FIELD_SETTING_STATUS,
} from "../graphql/customFieldSetting";
import CustomFieldStatus from "enums/CustomFieldStatus";
import { FormikCustomField } from "./customFields.types";

type UseCustomFieldEditorParams = {
  customFieldId?: string;
};

// Hook for admin UIs that create, edit, or status-toggle a custom field
// DEFINITION (the "shell").
//
// Pass `customFieldId` to edit an existing field; omit it to create.
const useCustomFieldEditor = ({
  customFieldId,
}: UseCustomFieldEditorParams) => {
  const { t } = useTranslation();

  // ============================================
  // QUERIES & MUTATIONS
  // ============================================

  const [
    getCustomFieldSetting,
    { data: customFieldSetting, loading: loadingCustomField },
  ] = useLazyQuery(GET_CUSTOM_FIELD_SETTING, {
    fetchPolicy: "cache-and-network",
  });

  const [createCustomFieldSetting, { loading: creatingCustomField }] =
    useMutation(CREATE_CUSTOM_FIELD_SETTING);

  const [updateCustomFieldSetting, { loading: updatingCustomField }] =
    useMutation(UPDATE_CUSTOM_FIELD_SETTING);

  const [
    updateCustomFieldSettingStatus,
    { loading: updatingCustomFieldStatus },
  ] = useMutation(UPDATE_CUSTOM_FIELD_SETTING_STATUS);

  // ============================================
  // FORMIK
  // ============================================

  const initialValues: FormikCustomField = {
    id: customFieldId,
    parentTypes: [],
    label: "",
    type: "",
    allowMultipleEntries: false,
    isRequired: false,
    options: [],
    status: CustomFieldStatus.ACTIVE,
    order: 1,
  };

  const formik = useFormik<FormikCustomField>({
    initialValues,
    validationSchema: customFieldValidations,
    onSubmit: async (values) => {
      const input = {
        parentTypes: values.parentTypes,
        label: values.label,
        type: values.type,
        allowMultipleEntries: values.allowMultipleEntries,
        isRequired: values.isRequired,
        ...(values.options &&
          values.options.length > 0 && {
            options: values.options.filter((option) => option.trim() !== ""),
          }),
        status: values.status,
        order: values.order,
      };

      try {
        if (values.id) {
          await handleUpdateCustomField(values.id, input);
        } else {
          await handleCreateCustomField(input);
        }
        return true;
      } catch (error) {
        return false;
      }
    },
  });

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    if (
      customFieldSetting &&
      customFieldSetting.customFieldSetting &&
      customFieldId
    ) {
      formik.resetForm({ values: customFieldSetting.customFieldSetting });
    } else if (!customFieldId) {
      formik.resetForm({ values: initialValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFieldSetting, customFieldId]);

  useEffect(() => {
    if (customFieldId) {
      getCustomFieldSetting({ variables: { id: customFieldId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFieldId]);

  // ============================================
  // MUTATION HANDLERS
  // ============================================

  const handleCreateCustomField = async (input: FormikCustomField) => {
    try {
      const response = await createCustomFieldSetting({ variables: { input } });
      toast.success(t("Custom field created successfully"));
      return response.data.createCustomFieldSetting.id;
    } catch (error: any) {
      toast.error("Error creating custom field: " + error.message);
      throw error;
    }
  };

  const handleUpdateCustomField = async (
    id: string,
    input: FormikCustomField,
  ) => {
    try {
      await updateCustomFieldSetting({ variables: { id, input } });
      toast.success(t("Custom field updated successfully"));
      return id;
    } catch (error: any) {
      toast.error(t("errorUpdatingCustomField") + error.message);
      throw error;
    }
  };

  const handleUpdateCustomFieldSettingStatus = async (
    id: string,
    status: CustomFieldStatus,
  ) => {
    try {
      await updateCustomFieldSettingStatus({ variables: { id, status } });
      toast.success(t("Custom field status updated successfully"));
      return id;
    } catch (error: any) {
      toast.error(
        t("ErrorUpdatingCustomFieldStatus", { message: error.message }),
      );
      throw error;
    }
  };

  const handleFieldStatusActionWithModal = (
    action:
      | CustomFieldStatus.ACTIVE
      | CustomFieldStatus.READONLY
      | CustomFieldStatus.DISABLED,
    fieldId: string,
    openModal: (
      title: string,
      description: string,
      onConfirm: () => void,
    ) => void,
    onSuccess?: () => void,
  ) => {
    const actionMap = {
      ACTIVE: {
        title: t("Reactivate Field"),
        description: t("ReactivateFieldDescription"),
        handler: handleUpdateCustomFieldSettingStatus,
      },
      READONLY: {
        title: t("Set Field to Read Only"),
        description: t("ReadOnlyFieldDescription"),
        handler: handleUpdateCustomFieldSettingStatus,
      },
      DISABLED: {
        title: t("Disable Field"),
        description: t("DisableFieldDescription"),
        handler: handleUpdateCustomFieldSettingStatus,
      },
    };

    const { title, description, handler } = actionMap[action];

    openModal(title, description, async () => {
      await handler(fieldId, action);
      if (onSuccess) onSuccess();
    });
  };

  // ============================================
  // LOADING STATES
  // ============================================

  const specificLoadingStates = {
    loadingCustomField,
    creatingCustomField,
    updatingCustomField,
    updatingCustomFieldStatus,
  };

  const isAnyLoading = Object.values(specificLoadingStates).some(
    (loading) => loading,
  );

  return {
    formik,
    specificLoadingStates,
    isAnyLoading,
    handleUpdateCustomFieldSettingStatus,
    handleFieldStatusActionWithModal,
  };
};

export default useCustomFieldEditor;
